import { useQuery } from '@tanstack/react-query';
import { AdminService, MediaAdminService, PipelineAttemptsService } from 'app/API';
import { cancelOnSignal } from 'app/API/core/CancelablePromise';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import type { TCall } from 'app/queries/types/TCall';
import { useMemo } from 'react';
import type { TAdminJobDetails } from '../types/TAdminJobDetails';
import { useAdminJobQuery } from './useAdminJobQuery';

export const useAdminJobDetailsQuery = (call: TCall, jobId: string) => {
  const enabled = Boolean(jobId);
  const job = useAdminJobQuery(call, jobId);
  const media = useQuery({
    queryKey: adminJobsKeys.domain(jobId, 'media'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(MediaAdminService.getAdminJobMedia({ jobId }), signal), { dontRedirectOnError: true }),
    enabled,
  });
  const timing = useQuery({
    queryKey: adminJobsKeys.domain(jobId, 'timing'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(PipelineAttemptsService.getAdminJobPipelineTimestamps({ jobId }), signal), {
        dontRedirectOnError: true,
      }),
    enabled,
  });
  const identifiers = useQuery({
    queryKey: adminJobsKeys.domain(jobId, 'identifiers'),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminJobTranscriptionIdentifiers({ jobId }), signal), {
        dontRedirectOnError: true,
      }),
    enabled,
  });
  const workspaceId = job.data?.workspaceId;
  const organization = useQuery({
    queryKey: adminJobsKeys.domain(jobId, 'organization', workspaceId),
    queryFn: ({ signal }) => {
      if (!workspaceId) {
        throw new Error('Job workspace is missing');
      }
      return call(() => cancelOnSignal(AdminService.getAdminOrganizationByWorkspace({ workspaceId }), signal), {
        dontRedirectOnError: true,
      });
    },
    enabled: enabled && Boolean(workspaceId),
  });
  const pipelineAttemptId = timing.data?.jobPipelineTimestamps?.attemptId ?? undefined;
  const diagnostics = useQuery({
    queryKey: adminJobsKeys.domain(jobId, 'diagnostics', pipelineAttemptId),
    queryFn: ({ signal }) =>
      call(() => cancelOnSignal(AdminService.getAdminJobTranscriptDiagnostics({ jobId, pipelineAttemptId }), signal), {
        dontRedirectOnError: true,
      }),
    enabled: enabled && !timing.isPending,
  });
  const data: TAdminJobDetails | undefined = useMemo(
    () =>
      job.data
        ? { ...job.data, ...media.data, ...timing.data, ...identifiers.data, ...organization.data, ...diagnostics.data }
        : undefined,
    [job.data, media.data, timing.data, identifiers.data, organization.data, diagnostics.data],
  );
  const domains = { media, timing, identifiers, organization, diagnostics };
  const failedDomains = Object.entries(domains).filter(([, query]) => query.isError);
  return {
    isLoading: job.isLoading,
    isError: job.isError,
    isSuccess: job.isSuccess,
    data,
    isFetching: job.isFetching,
    isDomainFetching: Object.values(domains).some((query) => query.isFetching),
    failedDomains,
    refetchDomain: (name: string) => failedDomains.find(([domain]) => domain === name)?.[1].refetch(),
    refetchDomains: () => Promise.all(Object.values(domains).map((query) => query.refetch())),
  };
};
